import { useAuthFrontendApis } from '@propelauth/frontend-apis-react';
import { useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface CompleteProfileFormProps {
  onSuccess?: () => void;
}

export default function CompleteProfileForm({ onSuccess }: CompleteProfileFormProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { updateUserMetadata } = useAuthFrontendApis();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter both first and last name');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      await updateUserMetadata({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });

      if (onSuccess) {
        onSuccess();
      } else {
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Failed to update user metadata:', error);
      setError('Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label htmlFor="firstName" className="text-sm font-medium text-neutral-950">
            First Name
          </Label>
          <Input
            id="firstName"
            type="text"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            placeholder="Enter your first name"
            required
            disabled={isLoading}
            className="h-11 border-neutral-200 focus:ring-black focus:border-black text-neutral-950"
          />
        </div>

        <div>
          <Label htmlFor="lastName" className="text-sm font-medium text-neutral-950">
            Last Name
          </Label>
          <Input
            id="lastName"
            type="text"
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            placeholder="Enter your last name"
            required
            disabled={isLoading}
            className="h-11 border-neutral-200 focus:ring-black focus:border-black text-neutral-950"
          />
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Button
        type="submit"
        disabled={isLoading || !firstName.trim() || !lastName.trim()}
        className="w-full h-11 bg-black hover:bg-gray-800 font-medium"
      >
        {isLoading ? 'Completing Profile...' : 'Complete Profile'}
      </Button>
    </form>
  );
}
